import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_BASE = (() => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:9000/api';
  return base.endsWith('/api') ? base : `${base}/api`;
})();

const BookSeat = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [seats, setSeats] = useState([]);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookingTime, setBookingTime] = useState(new Date().toTimeString().slice(0, 5));
  const [notes, setNotes] = useState('');

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userId = userInfo?.id || userInfo?._id;

  // Get token
  const token =
    userInfo?.token ||
    localStorage.getItem('token') ||
    localStorage.getItem('userToken');

  useEffect(() => {
    if (!userInfo || !userId || !token) {
      navigate('/login');
    }
  }, [userInfo, userId, token, navigate]);

  // Fetch clubs
  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.get(`${API_BASE}/clubs`, config);
      setClubs(data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      toast.error(error.response?.data?.message || 'Error fetching clubs');
      setLoading(false);
    }
  };

  // Fetch tables when club is selected
  useEffect(() => {
    if (selectedClub) {
      fetchTables();
    }
  }, [selectedClub]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.get(
        `${API_BASE}/bookings/available-tables/${selectedClub}`,
        config
      );
      setTables(data.data);
      setSelectedTable(null);
      setSeats([]);
      setSelectedSeat(null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error(error.response?.data?.message || 'Error fetching tables');
      setLoading(false);
    }
  };

  // Fetch seats when table is selected
  useEffect(() => {
    if (selectedTable) {
      fetchSeats();
    }
  }, [selectedTable]);

  const fetchSeats = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.get(
        `${API_BASE}/bookings/available-seats/${selectedTable}`,
        config
      );
      setSeats(data.data);
      setSelectedSeat(null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching seats:', error);
      toast.error(error.response?.data?.message || 'Error fetching seats');
      setLoading(false);
    }
  };

  const handleBookSeat = async () => {
    if (!selectedClub || !selectedTable || !selectedSeat) {
      toast.error('Please select club, table, and seat');
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      const bookingData = {
        clubId: selectedClub,
        tableId: selectedTable,
        seatId: selectedSeat,
        bookingDate,
        bookingTime,
        bookingType: 'Instant',
        notes,
      };

      const { data } = await axios.post(
        `${API_BASE}/bookings`,
        bookingData,
        config
      );

      toast.success('Seat booked successfully!');
      setLoading(false);

      // Navigate to my bookings or dashboard
      navigate('/my-bookings');
    } catch (error) {
      console.error('Error booking seat:', error);
      toast.error(error.response?.data?.message || 'Error booking seat');
      setLoading(false);
    }
  };

  const getSeatStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-500';
      case 'Occupied':
        return 'bg-red-500';
      case 'Reserved':
        return 'bg-yellow-500';
      case 'Blocked':
        return 'bg-gray-500';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-cm-bg text-cm-text pb-20">
      {/* Header */}
      <div className="bg-cm-surface text-cm-text border-b border-cm-border px-4 h-14 flex items-center sticky top-0 z-10">
        <div className="flex items-center">
          <button onClick={() => navigate('/dashboard')} className="mr-3 w-9 h-9 grid place-items-center rounded-cm bg-cm-surface-2" aria-label="Back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="font-display text-lg font-extrabold m-0">Book a Seat</h1>
        </div>
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        {/* Step 1: Select Club */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <h2 className="text-lg font-semibold mb-3 flex items-center">
            <span className="bg-cm-gold text-cm-gold-ink rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">
              1
            </span>
            Select Club
          </h2>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cm-gold"
            value={selectedClub || ''}
            onChange={(e) => setSelectedClub(e.target.value)}
            disabled={loading}
          >
            <option value="">Choose a club...</option>
            {clubs.map((club) => (
              <option key={club._id} value={club._id}>
                {club.name} - {club.clubCode}
              </option>
            ))}
          </select>
        </div>

        {/* Step 2: Select Table */}
        {selectedClub && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center">
              <span className="bg-cm-gold text-cm-gold-ink rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">
                2
              </span>
              Select Table
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {loading ? (
                <div className="text-center py-4">Loading tables...</div>
              ) : tables.length === 0 ? (
                <div className="text-center py-4 text-gray-500">No tables available</div>
              ) : (
                tables.map((table) => (
                  <div
                    key={table._id}
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                      selectedTable === table._id
                        ? 'border-cm-gold bg-cm-gold-soft'
                        : 'border-gray-200 hover:border-cm-gold'
                    } ${!table.isAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => table.isAvailable && setSelectedTable(table._id)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold">
                          {table.tableName} ({table.tableNumber})
                        </h3>
                        <p className="text-sm text-gray-600">{table.gameType}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-cm-gold-text">
                          {table.availableSeats} / {table.totalSeats} Available
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 3: Select Seat */}
        {selectedTable && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center">
              <span className="bg-cm-gold text-cm-gold-ink rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">
                3
              </span>
              Select Seat
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {loading ? (
                <div className="col-span-full text-center py-4">Loading seats...</div>
              ) : seats.length === 0 ? (
                <div className="col-span-full text-center py-4 text-gray-500">No seats available</div>
              ) : (
                seats.map((seat) => (
                  <button
                    key={seat._id}
                    className={`p-4 rounded-lg border-2 font-semibold transition-all ${
                      seat.status === 'Available'
                        ? selectedSeat === seat._id
                          ? 'border-cm-gold bg-cm-gold text-cm-gold-ink'
                          : 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                        : 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    onClick={() => seat.status === 'Available' && setSelectedSeat(seat._id)}
                    disabled={seat.status !== 'Available'}
                  >
                    <div className="text-center">
                      <div className="text-xl">💺</div>
                      <div className="text-sm mt-1">Seat {seat.seatNumber}</div>
                      <div className={`w-2 h-2 rounded-full mx-auto mt-1 ${getSeatStatusColor(seat.status)}`}></div>
                    </div>
                  </button>
                ))
              )}
            </div>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
                <span>Occupied</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
                <span>Reserved</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-1"></div>
                <span>Blocked</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Booking Details */}
        {selectedSeat && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center">
              <span className="bg-cm-gold text-cm-gold-ink rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm">
                4
              </span>
              Booking Details
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cm-gold"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cm-gold"
                  value={bookingTime}
                  onChange={(e) => setBookingTime(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cm-gold"
                  rows="3"
                  placeholder="Any special requests or notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Button */}
        {selectedSeat && (
          <button
            onClick={handleBookSeat}
            disabled={loading}
            className="w-full bg-cm-gold text-cm-gold-ink py-4 rounded-lg font-semibold hover:bg-cm-gold-hover transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Booking...' : 'Confirm Booking'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BookSeat;
